import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Card, Paper, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
    GithubOnboardingCatalogDefinitionId,
    NsgAutomationRulesProvisioningCatalogDefinitionID,
    oracleIaaSDefinitionId,
    pECatalogDefinitionId,
    ProjectOnboardCatalogDefinitionId,
    JFrogCatalogDefinitionId,
    DSCloningCatalogDefinitionId,
    PostgreSQLFlexibleDbCatalogDefinitionId,
    MySQLFlexibleDbCatalogDefinitionId,
    CosmosDbCatalogDefinitionId,
} from 'utils/catalogDefinitionId';
import VSTypeDetails from 'pages/provisioning/virtual-service/components/preview/VSTypeDetails';
import orderStyle from 'pages/orders/style';
import approvalStyle, { StyledTableCell } from 'components/order-summary-details/style';
import LabelValue from 'components/label-value';
import CostSplit from 'components/order-summary-details/cost-split';
import MutipleResource from 'pages/provisioning/private-endpoint-v2/components/Preview/MutipleResource';
import MultipleResourceNsg from 'pages/provisioning/nsg-automation-rules/components/Preview/MultipleResourceNsg';
import MembersRequested from './MembersRequested';
import ProjectOnboardingMembersDetails from '../project-onboarding-members-details';

const GetSummary = ({ catalog, selectedApprovalId }) => (
    <>
        {Object.keys(catalog?.summary).map((label) => (
            <React.Fragment key={label}>
                {label === 'cost' && (
                    <CostSplit key={label} data={catalog?.summary[label]} width={oracleIaaSDefinitionId?.includes(catalog?.catalogServiceCode) ? 228 : 178} />
                )}
                {![
                    'cost',
                    'members',
                    'uploadScan',
                    'Fortify / Snyk - Code Repository',
                    'codeRepository',
                    'uploadArchitecture',
                    'virtualService',
                    'projectDetails',
                    'memberAccess',
                    'Databases',
                    'Github Repository To Migrate',
                    'Github ArgoCD Repository To Migrate',
                    'Github Onboarding Teams',
                    'Github Onborading Groups',
                    'Github New Repo Config',
                    'Github SonarQube',
                    'Github SonarQube Integration',
                    'Github Jfrog Artifactory Integration',
                    'Github Sonar Application Name',
                    'Github Sonar Application Short Name',
                    'Github Jfrog Dependency Management',
                    'Github Jfrog Repo Types',
                ].includes(label) && (
                    <Grid className="" key={label} xs={12} sx={approvalStyle.gridLabel} item>
                        <LabelValue
                            width={oracleIaaSDefinitionId?.includes(catalog?.catalogServiceCode) ? 228 : 178}
                            key={label}
                            label={`${label}`}
                            value={catalog?.summary[label]}
                            isCamelToTitle={
                                !catalog?.catalogServiceCode?.startsWith('AWS') &&
                                !catalog?.catalogServiceCode?.startsWith('NUTANIC') &&
                                !catalog?.catalogServiceCode?.startsWith('DAY2GITHUB') &&
                                !CosmosDbCatalogDefinitionId.includes(catalog?.catalogServiceCode)
                            }
                        />
                    </Grid>
                )}
                {DSCloningCatalogDefinitionId?.includes(catalog?.catalogServiceCode) && label === 'projectDetails' && (
                    <>
                        <Grid className="" key={label} xs={12} sx={approvalStyle.gridLabel} item>
                            <Box className="label" width="41%" ml={1}>
                                Project Details:
                            </Box>
                        </Grid>
                        <Grid className="" key={label} xs={12} sx={approvalStyle.gridLabel} item>
                            <TableContainer component={Paper}>
                                <Table sx={approvalStyle.orderSummary.table.common} size="small" aria-label="project details">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Organization Name</TableCell>
                                            <TableCell>Team ID&apos;s</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Members</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {catalog?.summary[label]?.map((project, idx) => (
                                            <TableRow key={idx}>
                                                <StyledTableCell>{project?.org}</StyledTableCell>
                                                <StyledTableCell>{project?.teamIds?.join(', ')}</StyledTableCell>
                                                <StyledTableCell>{project?.name}</StyledTableCell>
                                                <StyledTableCell>
                                                    {project?.memberList?.map((member) => member?.name)?.join(', ') || 'No members assigned'}
                                                </StyledTableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </>
                )}
                {JFrogCatalogDefinitionId?.includes(catalog?.catalogServiceCode) && label === 'memberAccess' && (
                    <>
                        <Grid className="" key={label} xs={12} sx={approvalStyle.gridLabel} item>
                            <TableContainer component={Paper}>
                                <Table sx={approvalStyle.orderSummary.table.common} size="small" aria-label="member access details">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Member Name</TableCell>
                                            <TableCell>Member Access</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {catalog?.summary[label]?.map((m, idx) => (
                                            <TableRow key={idx}>
                                                <StyledTableCell>{m?.name || 'No members assigned'}</StyledTableCell>
                                                <StyledTableCell>{m?.memberAccess?.join(', ') || 'No access assigned'}</StyledTableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </>
                )}
                {label === 'Quantity' &&
                    [...PostgreSQLFlexibleDbCatalogDefinitionId, ...MySQLFlexibleDbCatalogDefinitionId]?.includes(catalog?.catalogServiceCode) &&
                    Array.isArray(catalog?.summary?.Databases) &&
                    catalog.summary.Databases.length > 0 && (
                        <>
                            <Grid className="" xs={12} sx={approvalStyle.gridLabel} item>
                                <Box className="label" width="41%" ml={1}>
                                    Databases:
                                </Box>
                            </Grid>
                            <Grid className="" xs={12} sx={approvalStyle.gridLabel} item>
                                <TableContainer component={Paper}>
                                    <Table sx={approvalStyle.orderSummary.table.common} size="small" aria-label="database details">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Database Name</TableCell>
                                                <TableCell>Charset</TableCell>
                                                <TableCell>Collation</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {catalog.summary.Databases.map((db, idx) => (
                                                <TableRow key={idx}>
                                                    <StyledTableCell>{db.name}</StyledTableCell>
                                                    <StyledTableCell>{db.charset}</StyledTableCell>
                                                    <StyledTableCell>{db.collation}</StyledTableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        </>
                    )}

                {label === 'uploadScan' && (
                    <Grid className="" key={label} xs={12} sx={approvalStyle.gridLabel} item>
                        <Box display="flex" className="key" flexWrap="wrap" p={1}>
                            <Box className="label" width="41%">
                                Uploaded Scan Evidence:
                            </Box>
                            <Box className="value" sx={{ wordBreak: 'break-all', width: '59%' }}>
                                {catalog?.summary?.uploadScan?.map((row, id) => (
                                    <li key={id} style={{ listStyle: 'none', marginTop: '5px' }}>
                                        <b>{id + 1})</b> {row?.fileName}
                                    </li>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                )}
                {label === 'uploadArchitecture' && catalog?.summary?.uploadArchitecture?.length > 0 && (
                    <Grid className="" key={label} xs={12} sx={approvalStyle.gridLabel} item>
                        <Box display="flex" className="uploadArchitecture" flexWrap="wrap" p={1}>
                            <Box className="label" width="41%">
                                Uploaded Architecture Document:
                            </Box>
                            <Box className="value" sx={{ wordBreak: 'break-all', width: '59%' }}>
                                {catalog?.summary?.uploadArchitecture?.map((row, id) => (
                                    <li key={id} style={{ listStyle: 'none', marginTop: '5px' }}>
                                        <b>{id + 1})</b> {row?.fileName}
                                    </li>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                )}
                {label === 'members' &&
                    catalog?.summary?.members?.length > 0 &&
                    ![...ProjectOnboardCatalogDefinitionId, ...GithubOnboardingCatalogDefinitionId, ...JFrogCatalogDefinitionId].includes(
                        catalog?.catalogServiceCode
                    ) && (
                        <Box flex="100%">
                            <Card sx={orderStyle?.orderRIProvisionDetails?.summaryMembersCard?.membersCard}>
                                <Typography component="span" sx={orderStyle?.orderRIProvisionDetails?.summaryMembersCard?.memberType}>
                                    Members:
                                </Typography>
                                <MembersRequested membersDetails={catalog?.summary?.members} />
                            </Card>
                        </Box>
                    )}
                {label === 'members' && ProjectOnboardCatalogDefinitionId?.includes(catalog?.catalogServiceCode) && (
                    <Box flex="100%">
                        <Card sx={orderStyle?.orderRIProvisionDetails?.summaryMembersCard?.projectOnboardingMembersCard}>
                            <Typography component="span" sx={orderStyle?.orderRIProvisionDetails?.summaryMembersCard?.memberType}>
                                Members:
                            </Typography>
                            <ProjectOnboardingMembersDetails membersDetails={catalog?.summary?.members} />
                        </Card>
                    </Box>
                )}

                {label === 'codeRepository' && (
                    <Grid className="" key={label} xs={12} sx={approvalStyle.gridLabel} item>
                        <Box display="flex" className="key" flexWrap="wrap" p={1}>
                            <Box className="label" width="41%">
                                Code Repository:
                            </Box>
                            <Box className="value" sx={{ wordBreak: 'break-all', width: '59%' }}>
                                {catalog?.summary?.codeRepository?.map((row, id) => (
                                    <li key={id} style={{ listStyle: 'none', marginTop: '5px' }}>
                                        <b>{id + 1})</b> {row}
                                    </li>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                )}
                {label === 'Fortify / Snyk - Code Repository' && (
                    <Grid className="" key={label} xs={12} sx={approvalStyle.gridLabel} item>
                        <Box display="flex" className="key" flexWrap="wrap" p={1}>
                            <Box className="label" width="41%">
                                Fortify / Snyk - Code Repository:
                            </Box>
                            <Box className="value" sx={{ wordBreak: 'break-all', width: '59%' }}>
                                {catalog?.summary?.['Fortify / Snyk - Code Repository']?.map((row, id) => (
                                    <li key={id} style={{ listStyle: 'none', marginTop: '5px' }}>
                                        <b>{id + 1})</b> {row}
                                    </li>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                )}
                {label === 'virtualService' && (
                    <Grid className="" key={label} xs={12} sx={approvalStyle.gridLabel} item>
                        {Object.keys(catalog?.summary[label])?.map((vsType) => (
                            <VSTypeDetails virtualServiceData={catalog?.summary[label]} vsType={vsType} />
                        ))}
                    </Grid>
                )}
            </React.Fragment>
        ))}
        {pECatalogDefinitionId.includes(catalog?.catalogServiceCode) && (
            <Grid className="" key="peMultiRes" xs={12} sx={approvalStyle.gridLabel} item>
                <>
                    <MutipleResource resources={catalog?.resources} defaultExpanded={false} isLastPanelOpen={false} isOrderPage />
                </>
            </Grid>
        )}
        {NsgAutomationRulesProvisioningCatalogDefinitionID.includes(catalog?.catalogServiceCode) && (
            <Grid className="" key="nsgMultiRes" xs={12} sx={approvalStyle.gridLabel} item>
                <>
                    <MultipleResourceNsg resources={catalog?.resources} defaultExpanded={false} isLastPanelOpen={false} />
                </>
            </Grid>
        )}
        {Object.keys(catalog?.summary)?.length % 2 !== 0 && (
            <>
                <Grid className="" xs={12} item key={selectedApprovalId}>
                    &nbsp;
                </Grid>
            </>
        )}
    </>
);

GetSummary.propTypes = {
    catalog: PropTypes.any,
    selectedApprovalId: PropTypes.any,
};

GetSummary.defaultProps = {
    catalog: undefined,
    selectedApprovalId: '',
};

export default React.memo(GetSummary);
